import { useRef, useState } from "react";
import {
  Check, ChevronDown, ChevronRight, CircleDot, Lock, MessageSquarePlus,
  Paperclip, Upload, X,
} from "lucide-react";
import type { DesignEvent, DesignFile } from "../types";

function fmt(dt: string | null): string {
  if (!dt) return "—";
  try {
    return new Date(dt).toLocaleString("pl-PL", {
      day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return dt;
  }
}

function eventLine(ev: DesignEvent, statusLabels?: Record<string, string>): string {
  switch (ev.kind) {
    case "status_change": {
      // to_status is a raw enum value ("submitted"); render its Polish label when known.
      const label = (ev.to_status && statusLabels?.[ev.to_status]) || ev.to_status;
      return `Status: ${label || ev.kind_display}`;
    }
    case "comment":
      return ev.body;
    case "file_added":
      return `Dodano plik: ${ev.attachment_name ?? ""}`;
    case "file_replaced":
      return `Wymieniono plik: ${ev.attachment_name ?? ""}`;
    case "file_removed":
      return `Usunięto plik: ${ev.attachment_name ?? ""}`;
    case "correction_requested":
      return `Zgłoszono korektę${ev.body ? `: ${ev.body}` : ""}`;
    case "confirmation":
      return `Decyzja: ${ev.decision_display}${ev.body ? ` — ${ev.body}` : ""}`;
    default:
      return ev.kind_display;
  }
}

function SideBadge({ side }: { side: "executor" | "internal" }) {
  const internal = side === "internal";
  return (
    <span
      className={`px-1 py-px rounded text-[9px] font-medium uppercase ${
        internal
          ? "bg-[var(--color-primary-dim)] text-[var(--color-primary)]"
          : "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
      }`}
    >
      {internal ? "GK" : "Wykonawca"}
    </span>
  );
}

function UploadButton({
  label, icon, onPick, disabled, multiple,
}: {
  label: string;
  icon: React.ReactNode;
  onPick: (files: File[]) => void;
  disabled?: boolean;
  multiple?: boolean;
}) {
  const ref = useRef<HTMLInputElement>(null);
  return (
    <>
      <input
        ref={ref}
        type="file"
        multiple={multiple}
        className="hidden"
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []);
          if (files.length) onPick(files);
          e.target.value = "";
        }}
      />
      <button
        type="button"
        disabled={disabled}
        onClick={() => ref.current?.click()}
        className="inline-flex items-center gap-1 text-xs text-[var(--color-primary)] hover:underline disabled:opacity-50"
      >
        {icon} {label}
      </button>
    </>
  );
}

export interface WorkUnitStateOption {
  value: string;
  label: string;
}

interface WorkUnitPanelProps {
  title: React.ReactNode;
  badges?: React.ReactNode;
  files: DesignFile[];
  timeline: DesignEvent[];
  // Maps a raw status value (e.g. "submitted") to its Polish label for the timeline.
  statusLabels?: Record<string, string>;
  canWrite: boolean;   // executor (assigned subcontractor OR internal DLC): files / comment / state
  canManage: boolean;  // GK PM/director: accept + request correction
  busy: boolean;
  // When set, the unit is frozen read-only (e.g. route acceptance was reverted) and this note
  // explains why right on the card — every control is hidden regardless of canWrite/canManage.
  lockedNote?: string;
  // executor state control
  state: string;
  stateLabel: string;
  stateColor: string;
  stateOptions?: WorkUnitStateOption[];
  onSetState?: (state: string) => void;
  // GK controls
  onAccept?: () => void;
  acceptLabel?: string;
  acceptDisabled?: boolean;
  onRequestCorrection?: (note: string) => void;
  // shared file / comment ops — files are multifile (several at once). Uploaded files are
  // append-only: once added they can't be replaced or removed (audit-complete submission).
  onAddFiles: (files: File[]) => void;
  onComment: (body: string) => void;
}

// One unit of design work (an opracowanie or the kosztorys): state control, multifile lifecycle,
// comment composer, GK accept / required-note correction, and the append-only timeline.
export function WorkUnitPanel({
  title, badges, files, timeline, statusLabels, canWrite, canManage, busy, lockedNote,
  state, stateLabel, stateColor, stateOptions, onSetState,
  onAccept, acceptLabel = "Weryfikacja pozytywna", acceptDisabled, onRequestCorrection,
  onAddFiles, onComment,
}: WorkUnitPanelProps) {
  const [comment, setComment] = useState("");
  const [correcting, setCorrecting] = useState(false);
  const [correctionNote, setCorrectionNote] = useState("");
  const [collapsed, setCollapsed] = useState(true);
  const activeFiles = files.filter((f) => f.state === "active");
  // A frozen unit keeps its content visible but suppresses every write/manage control.
  const locked = !!lockedNote;
  const writable = canWrite && !locked;
  const manageable = canManage && !locked;

  return (
    <div className="card overflow-hidden" style={{ borderLeft: `3px solid ${stateColor}` }}>
      <div className="p-3.5 space-y-3">
        {/* header: title + badges + state */}
        <div className="flex items-start gap-3">
          <button
            type="button"
            onClick={() => setCollapsed((v) => !v)}
            aria-expanded={!collapsed}
            title={collapsed ? "Rozwiń" : "Zwiń"}
            className="mt-0.5 shrink-0 text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
          >
            {collapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
          </button>
          <div className="min-w-0 flex-1">
            <div
              className="font-semibold text-[15px] text-[var(--color-text)] flex items-center gap-1.5 flex-wrap cursor-pointer"
              onClick={() => setCollapsed((v) => !v)}
            >
              <span className="inline-block h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: stateColor }} />
              {title}
              {badges}
            </div>
          </div>
          <div className="flex flex-col items-end gap-1 shrink-0">
            {writable && stateOptions && onSetState ? (
              <select
                className="input-base text-xs py-1 w-auto font-semibold"
                style={{ color: stateColor }}
                value={stateOptions.some((o) => o.value === state) ? state : ""}
                disabled={busy}
                onChange={(e) => e.target.value && onSetState(e.target.value)}
              >
                <option value="" disabled>{stateLabel}</option>
                {stateOptions.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            ) : (
              <span
                className="px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide text-white"
                style={{ backgroundColor: stateColor }}
              >
                {stateLabel}
              </span>
            )}
          </div>
        </div>

        {/* freeze notice — visible even when collapsed so the suspension is never missed */}
        {locked && (
          <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-2.5 py-1.5 text-xs text-amber-800 dark:border-amber-700/60 dark:bg-amber-900/20 dark:text-amber-300">
            <Lock size={13} className="mt-0.5 shrink-0" />
            <span>{lockedNote}</span>
          </div>
        )}

        {!collapsed && (
        <>
        {/* GK controls: accept + request correction (note required) */}
        {manageable && (onAccept || onRequestCorrection) && (
          <div className="flex items-center gap-3 rounded-md bg-[var(--color-bg-muted)] px-3 py-2 flex-wrap">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-[var(--color-text-muted)]">Weryfikacja GK ELEKTIM</span>
            {onAccept && (
              <button
                type="button"
                disabled={busy || acceptDisabled}
                onClick={onAccept}
                className="inline-flex items-center gap-0.5 text-xs text-green-600 font-medium hover:underline disabled:opacity-50"
              >
                <Check size={13} /> {acceptLabel}
              </button>
            )}
            {onRequestCorrection && (
              <button
                type="button"
                disabled={busy}
                onClick={() => setCorrecting((v) => !v)}
                className="inline-flex items-center gap-0.5 text-xs text-amber-600 font-medium hover:underline disabled:opacity-50"
              >
                <X size={13} /> Weryfikacja negatywna
              </button>
            )}
          </div>
        )}
        {correcting && onRequestCorrection && (
          <div className="flex items-start gap-2">
            <textarea
              className="input-base text-xs flex-1 min-h-[2.25rem]"
              rows={2}
              placeholder="Powód weryfikacji negatywnej — co poprawić? (wymagane)"
              value={correctionNote}
              onChange={(e) => setCorrectionNote(e.target.value)}
            />
            <button
              type="button"
              disabled={busy || !correctionNote.trim()}
              onClick={() => {
                onRequestCorrection(correctionNote.trim());
                setCorrectionNote("");
                setCorrecting(false);
              }}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded bg-amber-600 text-white text-xs font-medium hover:opacity-90 disabled:opacity-50"
            >
              Odeślij do poprawy
            </button>
          </div>
        )}

        {/* files (multifile) */}
        <div className="rounded-md border border-dashed border-[var(--color-border)] p-2.5">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-[var(--color-text-muted)]">
              Pliki ({activeFiles.length})
            </span>
            {writable && (
              <UploadButton
                label="Dodaj pliki"
                icon={<Upload size={13} />}
                disabled={busy}
                multiple
                onPick={(fs) => onAddFiles(fs)}
              />
            )}
          </div>
          {activeFiles.length === 0 ? (
            <p className="text-xs text-[var(--color-text-muted)]">
              Brak plików{writable ? " — możesz wgrać kilka naraz." : "."}
            </p>
          ) : (
            <ul className="space-y-1">
              {activeFiles.map((f) => (
                <li key={f.id} className="flex items-center gap-2 text-xs">
                  <Paperclip size={12} className="text-[var(--color-text-muted)] shrink-0" />
                  {f.url ? (
                    <a href={f.url} target="_blank" rel="noreferrer" className="text-[var(--color-primary)] hover:underline truncate">
                      {f.original_name}
                    </a>
                  ) : (
                    <span className="truncate">{f.original_name}</span>
                  )}
                  <span className="text-[var(--color-text-muted)] shrink-0">· {f.uploaded_by_name ?? ""}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* comment composer (executor + GK) */}
        {(writable || manageable) && (
          <div className="flex items-start gap-2">
            <textarea
              className="input-base text-xs flex-1 min-h-[2.25rem]"
              rows={1}
              placeholder="Dodaj komentarz…"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
            <button
              type="button"
              disabled={busy || !comment.trim()}
              onClick={() => { onComment(comment.trim()); setComment(""); }}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded bg-[var(--color-primary)] text-white text-xs font-medium hover:opacity-90 disabled:opacity-50"
            >
              <MessageSquarePlus size={13} /> Dodaj
            </button>
          </div>
        )}

        {/* timeline */}
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wide text-[var(--color-text-muted)] mb-1">
            Historia
          </div>
          {timeline.length === 0 && <p className="text-xs text-[var(--color-text-muted)]">Brak wpisów.</p>}
          <ul className="space-y-1.5">
            {[...timeline].reverse().map((ev) => (
              <li key={ev.id} className="flex items-start gap-2 text-xs">
                <CircleDot size={11} className="mt-0.5 text-[var(--color-text-muted)] shrink-0" />
                <div className="min-w-0">
                  <div className="text-[var(--color-text)] break-words">{eventLine(ev, statusLabels)}</div>
                  <div className="flex items-center gap-1.5 text-[10px] text-[var(--color-text-muted)]">
                    <SideBadge side={ev.author_side} />
                    <span>{ev.author_name}</span>
                    <span>· {fmt(ev.changed_at)}</span>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
        </>
        )}
      </div>
    </div>
  );
}
